---
title: "Sourcing and outreach"
description: "Find candidates instead of waiting for them — run a web Mission, enrich emails, draft outreach, and send from your own inbox without burning your domain."
category: "sourcing"
keywords:
  - "candidate sourcing"
  - "recruiting outreach"
  - "passive candidate sourcing"
  - "sourcing pipeline"
  - "find prospects"
schema: "HowTo"
nav:
  section: "guides"
  order: 40
---

Sourcing is where you go find people instead of waiting for them to apply. You describe the role, Chosen searches the web, and prospects stream in. You enrich their emails, draft outreach, and send it from your own inbox.

This is the section for the whole flow. Each page below is one step of it.

## How the pieces fit

The **Sourcing** tab in the left sidebar has two sub-tabs. **Search** runs Missions — natural-language searches against the web. **Prospects** is the table of everyone you've sourced so far, with its own search over that pool. A prospect lives in the sourcing pool until you import it; importing turns it into a candidate in your hiring pipeline.

The honest part: web-sourced data is a best guess. Emails need a separate enrichment step, work history is assembled from public profiles, and not every prospect resolves to a contactable address. The pages below say where the seams are.

## In this section

<Steps>
  <li>**[Find prospects](/guides/sourcing/find-prospects)** — internal smart search over your existing pool, and Missions for external web discovery. Start here.</li>
  <li>**[Email enrichment](/guides/sourcing/enrichment)** — prospects arrive without confirmed emails. This is how Chosen finds them, and why it's worth drafting before the address resolves.</li>
  <li>**[Outreach drafts](/guides/sourcing/outreach-drafts)** — an AI-written first message per prospect, edited and sent from your own inbox.</li>
  <li>**[Sending outreach](/guides/sourcing/outreach-sending)** — pacing and per-domain limits, the things that keep your outreach out of spam.</li>
  <li>**[The experience timeline](/guides/sourcing/experience-timeline)** — every public role a prospect held, plus auto-surfaced signals. A sanity check before you spend personalization effort.</li>
  <li>**[Prospect to candidate](/guides/sourcing/prospect-to-candidate)** — importing a prospect into your hiring pipeline, and what carries over.</li>
</Steps>

<Callout type="tip" title="Search your own pool before you search the web">
Internal smart search is free and the people in it already know your company exists. Run it first. Reach for a Mission when you've exhausted what you already have.
</Callout>
